<template>
  <div class="pdf-container">
    <PdfTopbar
      :title="fileName || ''"
      :source-url="selectedReference?.sourceUrl || ''"
      :external-id="selectedReference?.externalId || ''"
      :pdf-src="src"
      :displayVeevaButton="displayVeevaButton"
    />
    <div class="pdf-viewer-container">
      <BbLoadingIndicator v-if="isLoading" class="loading-indicator" />
      <div class="pdf-page-wrapper">
        <VuePdfEmbed
          ref="pdfViewer"
          class="pdf-viewer"
          :style="{ zoom: zoom, overflow: 'auto', maxWidth: '100%', maxHeight: '100%' }"
          :key="localCurrentPage"
          :source="src"
          :page="localCurrentPage"
          :scale="7"
          :width="500"
          annotation-layer
          @loaded="setTotalPages"
          @rendered="onPageRendered"
          @error="(err) => console.log('PDF load error:', err)"
          @loading-failed="(err) => console.log('PDF load error:', err)"
          @rendering-failed="(err) => console.log('PDF render error:', err)"
        />
      </div>
    </div>
    <PdfActionsGadget
      :current-page="localCurrentPage"
      :total-pages="totalPages"
      @update:zoomIn="zoomIn"
      @update:zoomOut="zoomOut"
      @update:prevPage="prevPage"
      @update:nextPage="nextPage"
    />
  </div>
</template>
<script>
import { computed, ref, watch } from 'vue';
import VuePdfEmbed from 'vue-pdf-embed/dist/index.essential.mjs';
import PdfActionsGadget from '~/components/pdfViewer/PdfActionsGadget';
import PdfTopbar from '~/components/pdfViewer/PdfTopbar';
import useDocuments from '~/composables/useDocuments';
import useTranslation from '~/composables/useTranslation';
import useModals from '~/composables/useModals';
import { addLanguageToFileName } from '~/utils/document-utils';
import validateUrl from '~/utils/url';

export default {
  components: { PdfTopbar, PdfActionsGadget, VuePdfEmbed },
  setup() {
    const { getPdfUrl, selectedReference } = useDocuments();
    const { currentLanguageCode } = useTranslation();
    const { previewDocumentState } = useModals();

    const pdfViewer = ref([]);
    const totalPages = ref(0);
    const src = ref('');
    const isLoading = ref(true);
    const localCurrentPage = ref(previewDocumentState.value.startPage);
    const zoom = ref(2);
    const pdfDocCache = ref(null);

    const fileName = computed(() =>
      currentLanguageCode.value
        ? addLanguageToFileName(selectedReference.value.name, currentLanguageCode.value)
        : selectedReference.value.name,
    );
    const displayVeevaButton = computed(
      () =>
        selectedReference.value.type !== 'documentTranslation' && selectedReference.value.externalId.includes('veeva'),
    );

    /* const executeSearch = () => {
      const snippetToHighlight = decodeFromBase64(selectedReference.value.snippet);
      if (snippetToHighlight) {
        setTimeout(() => {
          pdfViewer.value.pdfFindController.executeCommand('find', {
            caseSensitive: false,
            findPrevious: undefined,
            highlightAll: true,
            phraseSearch: true,
            query: snippetToHighlight,
          });
        }, 300); // Adjust delay as necessary
      }
    }; */

    const isDocumentInCurrentLanguage = () => {
      const ref = selectedReference.value;
      const current = currentLanguageCode.value;
      return ref && (ref.languageCode === current || ref.sourceLanguage === current);
    };

    const loadDocument = (externalId) => {
      const lang = isDocumentInCurrentLanguage() ? '' : currentLanguageCode.value;
      getPdfUrl(externalId, lang).then((url) => {
        src.value = validateUrl(url) ? url : '';
      });
    };

    const nextPage = () => (localCurrentPage.value < totalPages.value ? localCurrentPage.value++ : 1);

    const prevPage = () => (localCurrentPage.value > 1 ? localCurrentPage.value-- : 1);

    const zoomIn = () => {
      zoom.value = Math.min(zoom.value + 0.1, 15);
    };

    const zoomOut = () => {
      zoom.value = Math.max(zoom.value - 0.1, 2);
    };

    const setTotalPages = (event) => {
      totalPages.value = event.numPages;
      pdfDocCache.value = event;
      isLoading.value = false;
    };

    const navigateToPdfDestination = async (destName) => {
      try {
        const pdfDoc = pdfDocCache.value;
        if (!pdfDoc) return;
        const dest = await pdfDoc.getDestination(destName);
        if (dest) {
          const pageIndex = await pdfDoc.getPageIndex(dest[0]);
          localCurrentPage.value = pageIndex + 1;
        }
      } catch (err) {
        console.error('Failed to resolve PDF destination:', err);
      }
    };

    const onPageRendered = () => {
      // Select all pages
      const pages = document.querySelectorAll('.vue-pdf-embed__page');
      pages.forEach((page) => {
        const canvas = page.querySelector('canvas');
        const annotationLayer = page.querySelector('.annotationLayer');
        if (!annotationLayer || !canvas) return;
        // Ensure the page container is relative
        page.style.position = 'relative';
        // Set annotationLayer to overlay canvas
        annotationLayer.style.position = 'absolute';
        annotationLayer.style.top = '0';
        annotationLayer.style.left = '0';
        annotationLayer.style.width = canvas.offsetWidth + 'px';
        annotationLayer.style.height = canvas.offsetHeight + 'px';
        annotationLayer.style.pointerEvents = 'auto';
        // Position each <section> absolutely
        annotationLayer.querySelectorAll('section').forEach((section) => {
          const leftPercent = parseFloat(section.style.left);
          const topPercent = parseFloat(section.style.top);
          const widthPercent = parseFloat(section.style.width);
          const heightPercent = parseFloat(section.style.height);
          section.style.position = 'absolute';
          section.style.left = (leftPercent / 100) * canvas.offsetWidth + 'px';
          section.style.top = (topPercent / 100) * canvas.offsetHeight + 'px';
          section.style.width = (widthPercent / 100) * canvas.offsetWidth + 'px';
          section.style.height = (heightPercent / 100) * canvas.offsetHeight + 'px';
          section.style.zIndex = '10';
          // Make link inside clickable
          const link = section.querySelector('a');
          if (link) {
            link.style.pointerEvents = 'auto';
            link.style.cursor = 'pointer';
            link.onclick = async (e) => {
              e.preventDefault();
              const href = link.getAttribute('href') || '';
              if (href.startsWith('#')) {
                await navigateToPdfDestination(href.slice(1));
              } else {
                window.open(link.href, '_blank');
              }
            };
          }
        });
      });
    };

    watch(currentLanguageCode, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        loadDocument(selectedReference.value.externalId, newVal);
      }
    });

    watch(
      () => selectedReference.value?.externalId,
      (newId, oldId) => {
        if (newId !== oldId) {
          loadDocument(newId);
        }
      },
      { immediate: true },
    );

    return {
      pdfViewer,
      localCurrentPage,
      totalPages,
      src,
      isLoading,
      zoom,
      zoomIn,
      zoomOut,
      nextPage,
      prevPage,
      fileName,
      displayVeevaButton,
      setTotalPages,
      selectedReference,
      onPageRendered,
    };
  },
};
</script>
<style scoped lang="scss">
.pdf-container {
  /* Style your dialog/panel here */
  display: flex;
  flex-direction: column;
  height: 100vh; /* Take full viewport height */
  overflow: hidden; /* Prevent scrolling */
  width: 100%;
  position: fixed; /* Use fixed positioning */
  top: 0;
  left: 0;
  z-index: 1000; /* Ensure it's above other content */
  .pdf-viewer-container {
    flex-grow: 1;
    display: flex;
    justify-content: center; /* Center horizontally */
    align-items: center; /* Center vertically */
    //overflow: hidden; /* Ensure no scrolling within the PDF viewer itself */
    overflow: auto;
    background-color: $bb-neutral-0;
    padding-top: $bb-spacing-2;

    .loading-indicator {
      background-color: $bb-neutral-0;
    }

    .pdf-page-wrapper {
      position: relative;
      display: inline-block;
      max-width: 100%;
      max-height: calc(100vh - 60px);
    }

    .pdf-viewer {
      max-width: 100%; /* Ensure the PDF viewer does not overflow its container */
      max-height: 100%; /* Adapt based on the actual size of the viewer */

      :deep(.highlight) {
        background-color: yellow !important;
      }

      :deep(.annotationLayer a) {
        pointer-events: auto;
        cursor: pointer;
        text-decoration: underline;
        color: blue;
        width: 100%;
        height: 100%;
        display: block;
      }
    }
  }
}
</style>
